import React from 'react';

import { TabValue } from 'src/checkout/components/sections/payment/shared/context';
import {
  Badge,
  BadgeContent,
  BadgeLabel,
  Tab,
  TabBackground,
  TabContent,
  TabHeader,
  TabHeaderContent,
  TabsContainer,
  TabText,
  TabTextGroup,
} from './payment-tabs.styles';
import { TabOption } from '../use-selection';

type PaymentTabsProps = {
  children: React.ReactNode;
  onTabChange: (tab: string) => void;
  selectedTab: TabValue;
  tabOptions: TabOption[];
  showFeeBadgeInline?: boolean;
};

// Utils
export const buildTabBorderRadius = ({ selected, isFirst }: { selected: boolean; isFirst: boolean }): string => {
  if (selected) {
    return '8px 8px 0px 0px';
  }
  return isFirst ? '8px 0px 8px 0px' : '0px 8px 0px 8px';
};

const FeeBadge = ({ badgeLabel, badgeIcon }: { badgeLabel: string; badgeIcon?: React.ReactNode }): JSX.Element => (
  <Badge>
    <BadgeContent>
      {!!badgeIcon && badgeIcon}
      <BadgeLabel>{badgeLabel}</BadgeLabel>
    </BadgeContent>
  </Badge>
);

export const PaymentTabs: React.FC<PaymentTabsProps> = ({
  selectedTab,
  onTabChange,
  children,
  tabOptions,
  showFeeBadgeInline = false,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const currentIndex = tabOptions.findIndex((tab) => tab.value === selectedTab);
    const isNotLastTab = currentIndex !== tabOptions.length - 1;
    if (isNotLastTab && e.key === 'Tab') {
      onTabChange(tabOptions[currentIndex + 1].value);
    }
  };

  const firstTabValue = tabOptions.length > 0 ? tabOptions[0].value : '';
  const isFirstTabSelected = selectedTab === firstTabValue;

  return (
    <TabsContainer data-testid='payment-tabs'>
      <TabHeader firstTabSelected={isFirstTabSelected} role='tablist'>
        {tabOptions.map(({ value, label, sublabel, badgeLabel, badgeIcon }) => {
          const selected = selectedTab === value;
          const isFirst = value === firstTabValue;
          const tabBorderRadius = buildTabBorderRadius({ selected, isFirst });
          return (
            <TabBackground selected={selected} isFirst={isFirst} key={value}>
              <Tab
                role='tab'
                id={`tab-${value}`}
                aria-controls={`tabpanel-${value}`}
                aria-selected={selected}
                tabIndex={selected ? 0 : -1}
                selected={selected}
                $borderRadius={tabBorderRadius}
                onClick={() => onTabChange(value)}
                onKeyDown={handleKeyDown}
              >
                <TabHeaderContent>
                  <TabTextGroup gap={showFeeBadgeInline ? 2 : 1}>
                    <TabText size='medium' weight='semi' selected={selectedTab === value}>
                      {label}
                    </TabText>
                    {!!sublabel && (
                      <TabText size='medium' selected={selectedTab === value}>
                        {sublabel}
                      </TabText>
                    )}
                    {!!badgeLabel && showFeeBadgeInline && <FeeBadge badgeLabel={badgeLabel} badgeIcon={badgeIcon} />}
                  </TabTextGroup>
                  {!!badgeLabel && !showFeeBadgeInline && <FeeBadge badgeLabel={badgeLabel} />}
                </TabHeaderContent>
              </Tab>
            </TabBackground>
          );
        })}
      </TabHeader>
      <TabContent
        aria-labelledby={`tab-${selectedTab}`}
        id={`tabpanel-${selectedTab}`}
        role='tabpanel'
        selectedTab={selectedTab}
        isFirst={selectedTab === firstTabValue}
      >
        {children}
      </TabContent>
    </TabsContainer>
  );
};
